import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import Panel from "../../components/brand/Panel";
import {
  getPropertyLegalWording,
  previewPropertyLegalWording,
  updatePropertyLegalWording,
} from "../../api/financeApi";
import { getApiErrorMessage } from "../../utils/concurrency";
import LegalWordingEditor from "./LegalWordingEditor";

const MAX_LENGTH = 20000;
const MAX_HTML_LENGTH = 50000;

function visibleText(html) {
  if (!html) return "";
  const document = new DOMParser().parseFromString(html, "text/html");
  return (document.body.textContent || "").replace(/\u00a0/g, " ").trim();
}

export default function LegalWordingPanel({ propertyId, onMessage, onError }) {
  const [wording, setWording] = useState("");
  const [savedWording, setSavedWording] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [rowVersion, setRowVersion] = useState(null);
  const [baselineKey, setBaselineKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewSource, setPreviewSource] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewUrlRef = useRef("");

  const replacePreviewUrl = useCallback((nextUrl = "") => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = nextUrl;
    setPreviewUrl(nextUrl);
  }, []);

  useEffect(() => () => replacePreviewUrl(), [replacePreviewUrl]);

  const applyRecord = useCallback((data, key) => {
    const value = data.wording || "";
    setWording(value);
    setSavedWording(value);
    setIsDefault(Boolean(data.is_default));
    setRowVersion(data.row_version);
    setBaselineKey(key);
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        onError?.("");
        const response = await getPropertyLegalWording(propertyId);
        if (!active) return;
        applyRecord(
          response.data,
          `${propertyId}:${response.data.row_version ?? "default"}`
        );
      } catch (error) {
        if (active) {
          onError?.(getApiErrorMessage(error, "Failed to load legal wording."));
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [propertyId, onError, applyRecord]);

  const handleBaseline = useCallback((html) => {
    setWording(html);
    setSavedWording(html);
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      onError?.("");
      const response = await updatePropertyLegalWording(propertyId, {
        wording,
        row_version: rowVersion,
      });
      applyRecord(
        response.data,
        `${propertyId}:${response.data.row_version ?? "default"}:saved`
      );
      onMessage?.("Legal wording saved.");
    } catch (error) {
      onError?.(getApiErrorMessage(error, "Failed to save legal wording."));
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      setPreviewing(true);
      onError?.("");
      const response = await previewPropertyLegalWording(propertyId, wording);
      const url = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      replacePreviewUrl(url);
      setPreviewSource(
        response.headers["x-invoice-preview-source"] || "Owner invoice preview"
      );
      setPreviewOpen(true);
    } catch (error) {
      onError?.(getApiErrorMessage(error, "Failed to create invoice preview."));
    } finally {
      setPreviewing(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    replacePreviewUrl();
  };

  const downloadPreview = () => {
    const link = document.createElement("a");
    link.href = previewUrl;
    link.download = "owner-invoice-preview.pdf";
    link.click();
  };

  if (loading) return <Skeleton variant="rounded" height={360} />;

  const busy = saving || previewing;
  const visibleLength = visibleText(wording).length;
  const dirty = wording !== savedWording;
  const valid =
    visibleLength > 0 && visibleLength <= MAX_LENGTH && wording.length <= MAX_HTML_LENGTH;

  const actions = (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={!dirty || !valid || busy}
      >
        {saving ? "Saving…" : "Save"}
      </Button>
      <Button
        variant="outlined"
        startIcon={
          previewing ? <CircularProgress size={16} color="inherit" /> : <VisibilityOutlinedIcon />
        }
        onClick={handlePreview}
        disabled={!valid || busy}
      >
        {previewing ? "Preparing preview…" : "Preview full invoice"}
      </Button>
    </Box>
  );

  return (
    <>
      <Panel title="Owner invoice legal wording" right={actions}>
        <Box sx={{ p: 2.75 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <Chip
              size="small"
              color={isDefault ? "warning" : "success"}
              label={
                isDefault
                  ? "Shared default — not customised for this property"
                  : "Customised for this property"
              }
            />
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            {isDefault
              ? "This is the shared default, from “We accept monthly” to the end of the invoice. Save stores a copy for this property only. Preview shows the whole invoice and does not save."
              : "This wording is saved for this property and prints from “We accept monthly” to the end of the invoice. Preview shows the whole invoice and does not save."}
          </Alert>
          <LegalWordingEditor
            value={wording}
            baselineKey={baselineKey}
            onChange={setWording}
            onBaseline={handleBaseline}
            disabled={busy}
          />
          <Typography
            variant="caption"
            color={visibleLength > MAX_LENGTH ? "error" : "text.secondary"}
            sx={{ display: "block", mt: 1 }}
          >
            {visibleLength.toLocaleString()} / {MAX_LENGTH.toLocaleString()} characters
          </Typography>
        </Box>
      </Panel>

      <Dialog
        open={previewOpen}
        onClose={closePreview}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 400,
          "& .MuiDialog-paper": {
            height: "calc(100% - 32px)",
            maxHeight: "calc(100% - 32px)",
            m: "16px",
          },
        }}
      >
        <DialogTitle>Owner invoice preview</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 1, overflow: "hidden" }}
        >
          <Typography variant="body2" color="text.secondary">
            {previewSource}
          </Typography>
          {previewUrl && (
            <Box
              component="iframe"
              title="Owner invoice legal wording preview"
              src={previewUrl}
              sx={{ width: "100%", flex: 1, minHeight: 0, border: 0 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={downloadPreview}
            disabled={!previewUrl}
          >
            Download PDF
          </Button>
          <Button variant="outlined" startIcon={<CloseIcon />} onClick={closePreview}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
